import type { HoveredSeriesData } from "@/FundPage/components/Performance/types";
import { fontFamily, fontSize } from "@/styles/primitives";
import type { PerfAPIResponse, Period } from "@/types";
import Highcharts from "highcharts";
interface TooltipPointInstance extends Highcharts.Point {
	points: Array<Highcharts.Point>;
}

export default function createPerfChartConfig(
	data: PerfAPIResponse,
	colors?: string[],
	onHover?: (data: HoveredSeriesData[]) => void,
	periodSelcted?: Period,
): Highcharts.Options | null {
	const { share_class_info } = data;

	if (data.share_class_plots[0].data.length === 0) {
		return null;
	}

	const xMin = data.share_class_plots[0].data.at(0)?.[0];
	const xMax = data.share_class_plots[0].data.at(-1)?.[0];
	const yMin = Math.min(
		...data.share_class_plots.map((plot) =>
			Math.min(...plot.data.map((d) => d[1])),
		),
	);
	const yMax = Math.max(
		...data.share_class_plots.map((plot) =>
			Math.max(...plot.data.map((d) => d[1])),
		),
	);
	const shareClassSeries: Highcharts.SeriesOptionsType[] =
		data.share_class_plots.map((plot) => {
			return {
				type: "line",
				name:
					share_class_info.share_class_long_name ||
					share_class_info.share_class_name,
				data: plot.data,
				color: colors?.[0] ?? "#4F2D7F",
				lineWidth: 2,
				marker: {
					enabled: false,
				},
			};
		});

	// Build the "Index" (benchmark/sector) series
	const indexSeries: Highcharts.SeriesOptionsType[] = data.index_plots.map(
		(plot, i) => {
			return {
				type: "line",
				name: plot.name,
				data: plot.data,
				color: colors?.slice(1).at(i) ?? "#217c7e",
				dashStyle: "Solid",
				lineWidth: 1,
				marker: {
					enabled: false,
				},
			};
		},
	);

	const options: Highcharts.Options = {
		chart: {
			type: "line",
			spacing: [20, 0, 0, 0],
			panning: { enabled: false },
			zooming: { mouseWheel: false },
			height: 230,
			// animation: false,
		},
		title: undefined,
		subtitle: undefined,
		xAxis: {
			type: "datetime",
			// min: xMin,
			// max: xMax,
			tickInterval:
				(periodSelcted && periodSelcted === "10y" ? 2 : 1) *
				356 *
				24 *
				3600 *
				1000,
			crosshair: {
				color: "#333",
				dashStyle: "ShortDash",
				width: 1,
			},
			lineWidth: 0,
			tickWidth: 0,
			labels: {
				style: {
					fontFamily: fontFamily.sans,
					fontSize: fontSize.label.s.desktop,
				},
				formatter: function () {
					if (this.axis.max && this.axis.min) {
						const range = this.axis.max - this.axis.min;
						const oneYearMs = 1 * 356 * 24 * 3600 * 1000;
						const fourYearsMs = 4 * oneYearMs;
						if (range <= oneYearMs) {
							return Highcharts.dateFormat("%b", this.value as number);
						}
						if (range <= fourYearsMs) {
							return Highcharts.dateFormat("%b %Y", this.value as number);
						}
					}
					return Highcharts.dateFormat("%Y", this.value as number);
				},
			},
		},
		yAxis: {
			title: {
				text: "",
			},
			labels: {
				format: "{value}",
				style: {
					fontFamily: fontFamily.sans,
					fontSize: fontSize.label.s.desktop,
				},
			},
			// min: yMin,
			// max: yMax,
		},
		tooltip: {
			shared: true,
			xDateFormat: "%e %b %Y",
			useHTML: true,
			backgroundColor: "transparent",
			borderColor: "transparent",
			shadow: false,
			style: {
				padding: "0px",
			},
			formatter: function () {
				const _self = this as TooltipPointInstance;
				const fromX = Highcharts.dateFormat(
					"%e %b %Y",
					_self.series.data.at(0)?.x as number,
				);
				if (_self.points) {
					const hoveredData = _self.points.map((pt) => {
						return {
							name: pt.series.name,
							color: (pt.series.color || "#000") as string,
							value: pt.y as number,
						};
					});
					onHover?.(hoveredData);
				}

				return `<span style="font-size: 12px;"><b>${fromX}</b> to <b>${Highcharts.dateFormat("%e %b %Y", _self.x as number)}</b></span>`;
			},
			// positioner: function (labelWidth, labelHeight, point) {
			// 	if (!point) {
			// 		// If no point is hovered, fallback to (0,0) or any default
			// 		return { x: 0, y: 0 };
			// 	}

			// 	const chart = this.chart;

			// 	// 1) Compute a desired (x, y) so the tooltip is horizontally centered
			// 	//    on the crosshair and near the top of the plot area.
			// 	let x = chart.plotLeft + point.plotX - labelWidth / 2;
			// 	let y = chart.plotTop; // 10px from the top of the plot

			// 	// 2) Clamp X so the tooltip doesn't overflow to the left or right
			// 	const minX = chart.plotLeft;
			// 	const maxX = chart.plotLeft + chart.plotWidth - labelWidth;
			// 	x = Math.max(minX, Math.min(x, maxX));

			// 	// 3) Optionally clamp Y if you want to ensure it never overflows
			// 	//    above or below the plot area:
			// 	const minY = chart.plotTop;
			// 	const maxY = chart.plotTop + chart.plotHeight - labelHeight;
			// 	y = Math.max(minY, Math.min(y, maxY));

			// 	return { x, y };
			// },
		},
		legend: {
			enabled: false,
		},
		credits: {
			enabled: false,
		},
		plotOptions: {
			series: {
				animation: false,
				connectNulls: false, // true if gaps need to be connected
				point: {
					events: {
						mouseOver: function () {
							this.series.chart.yAxis[0].update({
								crosshair: {
									color: this.series.color,
									dashStyle: "ShortDash",
								},
							});
						},
						mouseOut: function () {
							this.series.chart.yAxis[0].update({
								crosshair: false,
							});
						},
					},
				},
				marker: {
					enabled: false,
					symbol: "circle",
				},
				events: {
					mouseOut() {
						onHover?.([]);
					},
				},
			},
		},
		scrollbar: {
			enabled: false,
		},
		series: [...shareClassSeries, ...indexSeries],
	};
	return options;
}
